import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { MatDialogClose } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/ui-components/button/button.component';
import { CustomLinkDirective } from '@shared/directives/custom-link/custom-link.directive';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { appSelectors } from '@store/selectors/app.selectors';
import { LoadingIds } from '@shared/constants/loading';
import { LoadingPanelComponent } from '@shared/ui-components/loading-panel/loading-panel.component';
import { startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { userActions } from '@store/actions/user.actions';

@Component({
  selector: 'ac-impersonation-dialog',
  standalone: true,
  imports: [
    TranslateModule,
    ButtonComponent,
    CustomLinkDirective,
    MatDialogClose,
    MatInputModule,
    ReactiveFormsModule,
    LoadingPanelComponent,
    AsyncPipe,
  ],
  template: `
    <p class="title-m c-secondary-dark">{{ 'HEADER.IMPERSONATE.TITLE' | translate }}</p>
    <mat-form-field class="w-full mt-3 s:mt-4">
      <mat-label>{{ 'GENERAL.EMAIL' | translate }}</mat-label>
      <input matInput [formControl]="_emailControl" type="text" />
    </mat-form-field>
    <div class="flex flex-col-reverse items-center gap-2 mt-3 s:flex-row s:justify-end s:mt-4">
      <a acSecondaryLink mat-dialog-close>{{ 'GENERAL.CANCEL' | translate }}</a>
      <button primary-button small class="self-stretch !max-w-none" [disabled]="_isDisabled$ | async" (click)="_startImpersonation()">
        {{ 'HEADER.IMPERSONATE.START' | translate }}
      </button>
    </div>
    @if (_isLoadingSig()) {
      <ac-loading-panel />
    }
  `,
  styles: [
    `
      @use '/src/styles/functions';
      @use '/src/styles/mixins/breakpoints';

      :host {
        display: block;
        padding: functions.spacing(2);
        position: relative;

        @include breakpoints.mediaBreakpoint('s') {
          padding: functions.spacing(5);
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImpersonationDialogComponent {
  protected readonly _emailControl = new FormControl<string | null>(null, [Validators.required, Validators.email]);
  protected readonly _isLoadingSig = computed(() =>
    this._store.selectSignal(appSelectors.specificLoading)().includes(LoadingIds.IMPERSONATION)
  );
  protected readonly _isDisabled$ = this._emailControl.statusChanges.pipe(
    startWith(this._emailControl.status),
    map((value) => value !== 'VALID')
  );
  constructor(private readonly _store: Store<StoreState>) {}

  protected _startImpersonation() {
    const email = this._emailControl.value;
    if (this._emailControl.valid && email) {
      this._store.dispatch(userActions.fetchImpersonation({ impersonationEmail: email }));
    }
  }
}
